import {
  BanknotesIcon,
  BriefcaseIcon,
  BuildingStorefrontIcon,
  HomeIcon,
  TruckIcon,
  DocumentArrowUpIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon,
  RadioIcon,
  UsersIcon,
  ArchiveBoxIcon,
  DocumentChartBarIcon,
} from "@heroicons/react/24/outline";
import { hasAnyStoredPermission, hasStoredPermission } from "../utils/authority";
import { PERMISSIONS } from "../constants/access-control";
import {
  APP_TYPES,
  getProductTerminology,
} from "../constants/product-terminology";
import { JOBS_V2_FEATURE_KEY } from "../utils/featureFlags";

/**
 * Builds the primary nav menu. Each entry can opt into permission-based
 * visibility by setting `authority` to a `hasStoredPermission(...)` result.
 *
 * The Dashboard menu reads `entry.authority` as a boolean and hides items
 * that resolve to `false`. Items without `authority` are visible to everyone
 * (gated only by feature flag / appType).
 *
 * @param {Function} t i18n translator for non-terminology labels
 * @param {string} [appType] Tenant app type. Defaults to trucking so a missing
 *   argument cannot fall through `getProductTerminology` to Schedule.
 */
export const getMenuConfig = (t, appType) => {
  const terms = getProductTerminology(appType || APP_TYPES.TRUCKING);
  return [
  { title: t("dashboard.main"), path: "/", icon: HomeIcon },
  {
    title: terms.jobPlural,
    path: "/jobs",
    icon: BriefcaseIcon,
    authority: hasStoredPermission(PERMISSIONS.JOBS_READ),
    featureKey: "jobs.enabled",
  },
  {
    title: `${terms.job} workspace`,
    path: "/jobs/v2",
    icon: BriefcaseIcon,
    authority: hasStoredPermission(PERMISSIONS.JOBS_READ),
    featureKey: JOBS_V2_FEATURE_KEY,
    optIn: true,
  },
  {
    title: terms.navDispatch,
    path: "/dispatch",
    icon: RadioIcon,
    authority: hasStoredPermission(PERMISSIONS.DISPATCH_READ),
    featureKey: "dispatch.enabled",
  },
  {
    title: t("invoices.main"),
    path: "/invoices",
    icon: BanknotesIcon,
    authority: hasStoredPermission(PERMISSIONS.INVOICES_READ),
    featureKey: "invoices.enabled",
  },
  {
    title: "Manifests",
    path: "/manifests",
    icon: ClipboardDocumentListIcon,
    authority: hasStoredPermission(PERMISSIONS.MANIFESTS_READ),
    appTypes: ["trucking"],
    featureKey: "manifests.enabled",
  },
  {
    title: "Trip manifests",
    path: "/manifests/v2",
    icon: ClipboardDocumentListIcon,
    authority: hasStoredPermission(PERMISSIONS.MANIFESTS_READ),
    appTypes: ["trucking"],
    featureKey: "manifests.v2.enabled",
    optIn: true,
    // Keep the v2 routes; hide the sidebar/mobile item until trip manifests
    // are ready to ship in the trucking nav again.
    hideFromMenu: true,
  },
  {
    title: t("payouts.main"),
    path: "/payouts",
    icon: DocumentArrowUpIcon,
    authority: hasStoredPermission(PERMISSIONS.PAYOUTS_READ),
    appTypes: ["trucking", "serviceRepair"],
    featureKey: "payouts.enabled",
  },
  {
    title: t("clients.main"),
    path: "/clients",
    icon: BuildingStorefrontIcon,
    authority: hasStoredPermission(PERMISSIONS.CLIENTS_READ),
    featureKey: "clients.enabled",
  },
  {
    title: t("drivers.main"),
    path: "/drivers",
    icon: TruckIcon,
    authority: hasStoredPermission(PERMISSIONS.DRIVERS_READ),
    appTypes: ["trucking"],
    featureKey: "drivers.enabled",
  },
  {
    title: "Fleet",
    path: "/equipment",
    icon: TruckIcon,
    authority: hasStoredPermission(PERMISSIONS.EQUIPMENT_READ),
    appTypes: ["trucking"],
  },
  {
    title: t("employees.main"),
    path: "/employees",
    icon: UsersIcon,
    authority: hasStoredPermission(PERMISSIONS.EMPLOYEES_READ),
    appTypes: ["serviceRepair"],
    featureKey: "employees.enabled",
  },
  {
    title: "Reports",
    path: "/reports",
    icon: DocumentChartBarIcon,
    authority: hasStoredPermission(PERMISSIONS.REPORTS_READ),
    appTypes: ["trucking", "serviceRepair"],
    featureKey: "reports.enabled",
  },
  {
    title: "Inventory",
    path: "/inventory",
    icon: ArchiveBoxIcon,
    // Inventory permissions are retired. Visibility is driven by the
    // `inventory.enabled` feature flag only.
    appTypes: ["serviceRepair"],
    featureKey: "inventory.enabled",
  },
  {
    title: t("settings.main"),
    path: "/settings",
    icon: Cog8ToothIcon,
    authority: hasAnyStoredPermission([
      PERMISSIONS.SETTINGS_USERS_READ,
      PERMISSIONS.SETTINGS_USERS_MANAGE,
      PERMISSIONS.SETTINGS_ROLES_READ,
      PERMISSIONS.SETTINGS_ROLES_MANAGE,
      PERMISSIONS.SETTINGS_BILLING_READ,
      PERMISSIONS.SETTINGS_BILLING_MANAGE,
      PERMISSIONS.SETTINGS_TAGS_MANAGE,
      PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
      PERMISSIONS.SETTINGS_ORGANIZATION_MANAGE,
      PERMISSIONS.SETTINGS_ADVANCED_MANAGE,
    ]),
    featureKey: "settings.enabled",
  },
  ];
};
